import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Globals } from '../common/globals';
import * as pbi from 'powerbi-client';
import { forkJoin, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Honeycomb } from 'src/app/shared/honeycomb-api/honeycomb-api';
import { TranslationService } from '../shared/translation.service';
import {
  ChartConfig,
  DashboardCardPoint,
  DashboardChartPoint,
  DashboardColumn,
  DashboardColumnContent,
  DashboardConfig,
  DashboardRow,
  DashboardTab,
  DashboardWidget,
  WidgetType
} from './home-dashboard.models';
import { HomeDashboardService } from './home-dashboard.service';
import { HOME_DASHBOARD_THEME } from './home-dashboard.theme';
import {
  extractWidgetNames,
  getWidgetDataArray,
  selectVisibleWidgetNames
} from './home-dashboard.helpers';

@Component({
  selector: 'hc-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {


  @ViewChild('pbiview', {static: false}) pbiView: ElementRef;
  @ViewChildren('widgetHost') widgetHosts: QueryList<ElementRef<HTMLElement>>;
  private aad: Honeycomb.Tenant.Reports.IService.Model.PBI.AAD;
  private embed: pbi.Embed;
  private powerbi: pbi.service.Service;
  private pendingLegacyReportName: string;
  private pbiViewReady = false;
  private resizeObserver: ResizeObserver | null = null;
  private observedWidgetHosts = new Map<string, HTMLElement>();

  public readonly widgetTypes = WidgetType;
  public readonly dashboardTheme = HOME_DASHBOARD_THEME;
  public showPBIReport = false;
  public dashboard: DashboardConfig;
  public tabs: DashboardTab[] = [];
  public activeTabIndex = 0;
  public widgets: { [widgetName: string]: DashboardWidget } = {};
  public isLoading = true;
  public widgetWidths: { [widgetName: string]: number } = {};
  public widgetLoading: { [widgetName: string]: boolean } = {};
  private visibleWidgetNames = new Set<string>();

  constructor(
    private globals: Globals,
    private dashboardService: HomeDashboardService,
    private trans: TranslationService,
    @Inject('PBIReportController') private reportController: Honeycomb.Tenant.Reports.IService.PBIController,
    private cd: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    const homePBIReport = this.globals.getHomePBIReportName();
    if (homePBIReport) {
      this.pendingLegacyReportName = homePBIReport;
      this.powerbi = new pbi.service.Service(pbi.factories.hpmFactory, pbi.factories.wpmpFactory, pbi.factories.routerFactory);
      this.reportController.GetToken().subscribe(r => {
        this.aad = r;
        this.tryLoadLegacyReport();
      });
      return;
    }

    this.loadDashboard();
  }

  ngAfterViewInit() {
    this.pbiViewReady = true;
    this.tryLoadLegacyReport();

    this.initializeWidgetResizeHandling();
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.observedWidgetHosts.clear();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  public onViewportChanged() {
    this.syncVisibleWidgetNames();
    this.loadVisibleDashboardData();
  }

  private load(reportName: string) {
    this.reportController.GetReport(reportName).subscribe(report => {
      this.embed = this.powerbi.embed(this.pbiView.nativeElement, this.getPBIConfig(report));
      this.embed.reload();
      this.showPBIReport = true;
      this.cd.detectChanges();
    }, err => {
      console.log(err);
    });
  }

  private loadDashboard() {
    this.dashboardService.getDashboardLayout().pipe(
      switchMap(layout => {
        this.dashboard = layout;
        this.tabs = layout?.tabs || [];
        return this.dashboardService.getWidgetConfigs(extractWidgetNames(this.tabs));
      })
    ).subscribe(widgetConfigs => {
      this.widgets = this.initializeWidgets(widgetConfigs);
      this.initializeDashboardState();
      this.isLoading = false;

      this.cd.detectChanges();
      this.bindWidgetHosts();
      this.loadAllDashboardData();
    });
  }

  private tryLoadLegacyReport() {
    if (!this.pendingLegacyReportName || !this.aad || !this.pbiViewReady || !this.pbiView) {
      return;
    }

    const reportName = this.pendingLegacyReportName;
    this.pendingLegacyReportName = null;
    this.load(reportName);
  }

  private getPBIConfig(report: Honeycomb.Tenant.Reports.IService.Model.PBI.PBIReport) {
    return <pbi.IEmbedConfiguration>{
        type: 'report',
        id: report.reportID,
        viewMode: pbi.models.ViewMode.View,
        tokenType: pbi.models.TokenType.Aad,
        permissions: pbi.models.Permissions.All,
        pageView: 'actualSize',
        accessToken: this.aad.access_token,
        embedUrl: report.report.embedUrl + '&language=' + this.globals.getLanguage(),
        settings: {
            navContentPaneEnabled: true,
            layoutType: pbi.models.LayoutType.Master
        },
        pageName: 'DefaultPage'
    };
}

  public navTo(path: string) {
    this.globals.navigateClearTo(path);
  }

  public getRows(): DashboardRow[] {
    return this.tabs?.[this.activeTabIndex]?.rows || [];
  }

  public setActiveTab(index: number) {
    this.activeTabIndex = index;
    this.cd.detectChanges();
  }

  public getColumns(row: DashboardRow): DashboardColumn[] {
    return row && row.columns ? row.columns : [];
  }

  public isSection(column: DashboardColumn): boolean {
    return !!column && !!column.rows && column.rows.length > 0;
  }

  public getWidget(widgetName: string): DashboardWidget {
    return this.widgets && widgetName ? this.widgets[widgetName] : null;
  }

  public getSectionState(column: DashboardColumn): null {
    return null;
  }

  public isWidgetLoading(widget: DashboardWidget): boolean {
    return !!widget?.name && !!this.widgetLoading[widget.name];
  }

  public isWidgetPending(widget: DashboardWidget): boolean {
    return !!widget && widget.data === undefined && !this.isWidgetLoading(widget);
  }

  public getColumnFlex(row: DashboardRow, column: DashboardColumn): string {
    if (!row?.columns?.length || row.columns.length === 1) {
      return '1 1 100%';
    }

    return `${this.getColumnWeight(column)} 1 0%`;
  }

  public getColumnMinWidth(column: DashboardColumn): string {
    if (this.isSection(column)) {
      return '100%';
    }

    const widget = this.getWidget(column?.content?.ref);
    const itemCount = this.getWidgetDataCount(widget);

    switch (widget?.type) {
      case WidgetType.ChartCard:
        return '170px';
      case WidgetType.ChartPie:
        return itemCount > 4 ? '360px' : '320px';
      case WidgetType.ChartBar:
        return itemCount > 6 ? '540px' : '420px';
      case WidgetType.ChartLine:
      case WidgetType.ChartArea:
        return itemCount > 8 ? '520px' : '440px';
      default:
        return '260px';
    }
  }

  public getWidgetView(widget: DashboardWidget): [number, number] {
    const width = this.getWidgetWidth(widget);
    const height = this.getWidgetHeight(widget, width);

    switch (widget.type) {
      case WidgetType.ChartCard:
        return [width, height];
      case WidgetType.ChartPie:
        return [Math.min(width, 260), height - 50];
      case WidgetType.ChartBar:
      case WidgetType.ChartArea:
      case WidgetType.ChartLine:
      default:
        return [width, height];
    }
  }

  public getWidgetAccentColor(widget: DashboardWidget): string {
    return this.getWidgetTheme(widget).accentColor || '#69b35d';
  }

  public getWidgetScheme(widget: DashboardWidget): string {
    return this.getWidgetTheme(widget).chartScheme || 'cool';
  }

  public getWidgetCustomColors(widget: DashboardWidget): Array<{ name: string; value: string }> | null {
    if (widget?.type === WidgetType.ChartPie) {
      if (!this.hasWidgetData(widget)) {
        return [{ name: '', value: '#cccccc' }];
      }
      if (this.isAllZeroPieChart(widget)) {
        const grayShades = ['#b0b0b0', '#d4d4d4'];
        return this.aggregatePieChartByName(widget).map((r, i) => ({
          name: `${r.name}: 0`,
          value: grayShades[i % grayShades.length]
        }));
      }
    }

    const theme = this.getWidgetTheme(widget);
    if (theme.customColors && theme.customColors.length > 0) {
      return theme.customColors;
    }

    if (widget?.type === WidgetType.ChartArea || widget?.type === WidgetType.ChartLine) {
      return [{ name: this.getWidgetTitle(widget), value: this.getWidgetAccentColor(widget) }];
    }

    return null;
  }

  public getWidgetTitle(widget: DashboardWidget): string {
    if (!widget) {
      return '';
    }

    // label is a translatable key – used for display; name is internal key used for chart series
    if (widget.label?.trim()) {
      return widget.label.trim();
    }

    const name = widget.name ? String(widget.name).trim() : '';
    return name.length > 0 ? name : '';
  }

  public getPieChartTotal(widget: DashboardWidget): number {
    return this.aggregatePieChartByName(widget).reduce((sum, r) => sum + r.value, 0);
  }

  private aggregatePieChartByName(widget: DashboardWidget): DashboardChartPoint[] {
    const results = this.getChartResults(widget);
    const map = new Map<string, number>();
    for (const r of results) {
      map.set(r.name, (map.get(r.name) ?? 0) + r.value);
    }
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }

  private isAllZeroPieChart(widget: DashboardWidget): boolean {
    if (widget?.type !== WidgetType.ChartPie) { return false; }
    const aggregated = this.aggregatePieChartByName(widget);
    return aggregated.length > 0 && aggregated.every(r => r.value === 0);
  }

  public getPieChartResults(widget: DashboardWidget): DashboardChartPoint[] {
    const aggregated = this.aggregatePieChartByName(widget);
    if (aggregated.length === 0) {
      return [{ name: '', value: 1 }];
    }
    const allZero = aggregated.every(r => r.value === 0);
    return aggregated.map(point => ({
      name: `${point.name}: ${point.value.toLocaleString()}`,
      value: allZero ? 1 : point.value
    }));
  }

  public getChartResults(widget: DashboardWidget): DashboardChartPoint[] {
    const data = getWidgetDataArray(widget?.data);
    if (!widget || data.length === 0) {
      return [];
    }

    return data
      .map((row, index) => ({
        name: this.resolveChartPointName(row, index),
        value: this.resolveChartPointValue(row)
      }));
  }

  public getSeriesChartResults(widget: DashboardWidget, columnTitle?: string) {
    const series = this.getChartResults(widget);
    if (series.length === 0) {
      return [];
    }

    const name = columnTitle
      ? this.trans.instant(columnTitle) || columnTitle
      : this.getWidgetTitle(widget);

    return [{
      name,
      series
    }];
  }

  public getCardResults(widget: DashboardWidget): DashboardCardPoint[] {
    const data = getWidgetDataArray(widget?.data);
    if (!widget || data.length === 0) {
      return [];
    }

    return data.map((row, index) => ({
      name: data.length > 1 ? this.resolveChartPointName(row, index) : this.getWidgetTitle(widget),
      value: this.resolveChartPointValue(row),
      extra: {
        change: row.Change ?? row.change,
        changeLabel: row.ChangeLabel ?? row.changeLabel,
        unit: row.Unit ?? row.unit
      }
    }));
  }

  public hasWidgetData(widget: DashboardWidget): boolean {
    return getWidgetDataArray(widget?.data).length > 0;
  }

  public isWidgetDataMissing(widget: DashboardWidget): boolean {
    return !!widget && widget.data === null;
  }

  public trackByName(_: number, item: { name?: string; title?: string }) {
    return item && (item.name || item.title);
  }

  public isChartWidget(widget: DashboardWidget): boolean {
    return !!widget && widget.type !== WidgetType.ChartCard && widget.type !== WidgetType.Actions;
  }

  public reloadAllData() {
    this.invalidateWidgetData(Object.keys(this.widgets));
    this.loadAllDashboardData(true);
  }

  public onSelect(event: unknown) {
    console.log(event);
  }

  private initializeWidgets(sourceWidgets: { [widgetName: string]: DashboardWidget }): { [widgetName: string]: DashboardWidget } {
    return Object.keys(sourceWidgets || {}).reduce((result, widgetName) => {
      const widget = sourceWidgets[widgetName];

      result[widgetName] = {
        ...widget,
        name: widgetName,
        chartConfig: this.resolveWidgetChartConfig(widget?.chartConfig),
        data: undefined
      };

      return result;
    }, {} as { [widgetName: string]: DashboardWidget });
  }

  private initializeDashboardState() {
    this.widgetLoading = {};
    Object.keys(this.widgets).forEach(widgetName => {
      this.widgetLoading[widgetName] = false;
    });
  }

  private loadAllDashboardData(forceReload = false) {
    this.loadVisibleDashboardData(undefined, forceReload);
  }

  private loadVisibleDashboardData(widgetNames?: string[], forceReload = false) {
    const targetWidgetNames = widgetNames
      ? selectVisibleWidgetNames(widgetNames, this.visibleWidgetNames)
      : Array.from(this.visibleWidgetNames);
    const filteredWidgetNames = forceReload
      ? targetWidgetNames
      : targetWidgetNames.filter(widgetName => this.widgets[widgetName]?.data === undefined && !this.widgetLoading[widgetName]);

    if (filteredWidgetNames.length > 0) {
      this.loadWidgetData(filteredWidgetNames);
    }
  }

  private loadWidgetData(widgetNames: string[]) {
    const validWidgetNames = widgetNames.filter(name => !!this.widgets[name]?.dataSource?.name);
    if (validWidgetNames.length === 0) {
      return;
    }

    this.setWidgetLoading(validWidgetNames, true);

    const queries = validWidgetNames.reduce((acc, widgetName) => {
      const widget = this.widgets[widgetName];
      acc[widgetName] = this.dashboardService.executeQuery(
        widget.dataSource.name,
        { ...widget.dataSource.params }
      );
      return acc;
    }, {} as { [name: string]: Observable<any[]> });

    forkJoin(queries).subscribe(results => {
      this.applyWidgetData(validWidgetNames, results);
      this.cd.detectChanges();
      this.bindWidgetHosts();
    });
  }

  private applyWidgetData(widgetNames: string[], data: { [widgetName: string]: any }) {
    widgetNames.forEach(widgetName => {
      if (!this.widgets[widgetName]) {
        return;
      }

      this.widgets[widgetName] = {
        ...this.widgets[widgetName],
        data: Object.prototype.hasOwnProperty.call(data, widgetName) ? data[widgetName] : null
      };

      this.widgetLoading[widgetName] = false;
    });
  }

  private setWidgetLoading(widgetNames: string[], isLoading: boolean) {
    widgetNames.forEach(widgetName => {
      this.widgetLoading[widgetName] = isLoading;
    });
  }

  private invalidateWidgetData(widgetNames: string[]) {
    (widgetNames || []).forEach(widgetName => {
      if (!this.widgets[widgetName]) {
        return;
      }

      this.widgets[widgetName] = {
        ...this.widgets[widgetName],
        data: undefined
      };
      this.widgetLoading[widgetName] = false;
    });
  }

  private resolveChartPointName(row: Record<string, any>, index: number): string {
    const rawName = row.Name ?? row.name;
    const normalizedName = rawName !== null && rawName !== undefined ? String(rawName).trim() : '';
    return normalizedName.length > 0 ? normalizedName : `Item ${index + 1}`;
  }

  private resolveChartPointValue(row: Record<string, any>): number {
    const rawValue = Number(row.Value ?? row.value ?? 0);
    return Number.isFinite(rawValue) ? rawValue : 0;
  }

  private getWidgetTheme(widget: DashboardWidget) {
    if (widget?.type == null) {
      return {};
    }

    return this.dashboardTheme.typeThemes[widget.type] || {};
  }

  private resolveWidgetChartConfig(chartConfig?: ChartConfig): ChartConfig {
    return {
      ...this.createDefaultChartConfig(),
      ...(chartConfig || {})
    };
  }

  private createDefaultChartConfig(): ChartConfig {
    return {
      legend: false,
      showLabels: true,
      animations: true,
      gradient: false,
      xAxis: false,
      yAxis: false,
      showYAxisLabel: false,
      showXAxisLabel: false,
      xAxisLabel: '',
      yAxisLabel: '',
      timeline: false
    };
  }

  private initializeWidgetResizeHandling() {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(entries => {
        let hasChanges = false;

        for (const entry of entries) {
          const element = entry.target as HTMLElement;
          const widgetName = element.dataset.widgetName;
          const width = Math.floor(entry.contentRect.width);

          if (!widgetName || width <= 0 || this.widgetWidths[widgetName] === width) {
            continue;
          }

          this.widgetWidths[widgetName] = width;
          hasChanges = true;
        }

        if (hasChanges && !this.cd['destroyed']) {
          this.cd.detectChanges();
        }
      });
    }

    if (this.widgetHosts) {
      this.widgetHosts.changes.subscribe(() => this.bindWidgetHosts());
    }

    this.bindWidgetHosts();
  }

  private bindWidgetHosts() {
    if (!this.widgetHosts) {
      return;
    }

    const activeWidgetNames = new Set<string>();

    this.widgetHosts.forEach(hostRef => {
      const element = hostRef.nativeElement;
      const widgetName = element.dataset.widgetName;
      if (!widgetName) {
        return;
      }

      activeWidgetNames.add(widgetName);

      const currentWidth = Math.floor(element.getBoundingClientRect().width);
      if (currentWidth > 0) {
        this.widgetWidths[widgetName] = currentWidth;
      }

      const observedElement = this.observedWidgetHosts.get(widgetName);
      if (this.resizeObserver && observedElement !== element) {
        if (observedElement) {
          this.resizeObserver.unobserve(observedElement);
        }

        this.observedWidgetHosts.set(widgetName, element);
        this.resizeObserver.observe(element);
      }
    });

    Array.from(this.observedWidgetHosts.entries()).forEach(([widgetName, element]) => {
      if (activeWidgetNames.has(widgetName)) {
        return;
      }

      if (this.resizeObserver) {
        this.resizeObserver.unobserve(element);
      }

      this.observedWidgetHosts.delete(widgetName);
      delete this.widgetWidths[widgetName];
    });

    this.syncVisibleWidgetNames();
    this.loadVisibleDashboardData();

    if (!this.cd['destroyed']) {
      this.cd.detectChanges();
    }
  }

  private syncVisibleWidgetNames() {
    if (!this.widgetHosts) {
      return;
    }

    const nextVisibleWidgetNames = new Set<string>();

    this.widgetHosts.forEach(hostRef => {
      const element = hostRef.nativeElement;
      const widgetName = element.dataset.widgetName;

      if (!widgetName || !this.isWidgetVisible(element)) {
        return;
      }

      nextVisibleWidgetNames.add(widgetName);
    });

    this.visibleWidgetNames = nextVisibleWidgetNames;
  }

  private isWidgetVisible(element: HTMLElement): boolean {
    if (!element) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
  }

  private getColumnWeight(column: DashboardColumn): number {
    if (this.isSection(column)) {
      return 1;
    }

    const widget = this.getWidget(column?.content?.ref);
    const itemCount = this.getWidgetDataCount(widget);

    switch (widget?.type) {
      case WidgetType.ChartCard:
        return 0.8;
      case WidgetType.ChartPie:
        return 1.1 + Math.min(itemCount, 6) * 0.08;
      case WidgetType.ChartBar:
        return 1.25 + Math.min(itemCount, 10) * 0.1;
      case WidgetType.ChartLine:
      case WidgetType.ChartArea:
        return 1.35 + Math.min(itemCount, 12) * 0.07;
      case WidgetType.Actions:
        return 1;
      default:
        return 1;
    }
  }

  private getWidgetWidth(widget: DashboardWidget): number {
    const measuredWidth = widget?.name ? this.widgetWidths[widget.name] : 0;
    const safeWidth = measuredWidth > 0 ? measuredWidth - 8 : this.getFallbackWidgetWidth(widget);

    return Math.max(180, Math.round(safeWidth));
  }

  private getWidgetHeight(widget: DashboardWidget, width: number): number {
    const itemCount = this.getWidgetDataCount(widget);

    switch (widget?.type) {
      case WidgetType.ChartCard:
        return 188;
      case WidgetType.ChartPie: {
        const legendRows = widget?.chartConfig?.legend ? Math.ceil(Math.max(itemCount, 1) / (width < 420 ? 1 : 2)) : 0;
        const compactBoost = width < 360 ? 40 : 0;
        return Math.min(230, Math.max(200, 175 + legendRows * 20 + compactBoost));
      }
      case WidgetType.ChartBar: {
        const perItemHeight = width < 420 ? 18 : 14;
        const axisBoost = widget?.chartConfig?.xAxis ? 36 : 0;
        return Math.min(320, Math.max(220, 190 + axisBoost + itemCount * perItemHeight));
      }
      case WidgetType.ChartLine:
      case WidgetType.ChartArea: {
        const axisBoost = (widget?.chartConfig?.xAxis ? 30 : 0) + (widget?.chartConfig?.yAxis ? 20 : 0);
        const compactBoost = width < 420 ? 40 : 0;
        return Math.min(300, Math.max(220, 200 + axisBoost + compactBoost));
      }
      default:
        return 220;
    }
  }

  private getFallbackWidgetWidth(widget: DashboardWidget): number {
    switch (widget?.type) {
      case WidgetType.ChartCard:
        return 240;
      case WidgetType.ChartPie:
        return 320;
      case WidgetType.ChartBar:
        return 420;
      case WidgetType.ChartLine:
      case WidgetType.ChartArea:
        return 460;
      default:
        return 280;
    }
  }

  private getWidgetDataCount(widget: DashboardWidget): number {
    return getWidgetDataArray(widget?.data).length;
  }
}
